import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useConfig } from '@entities/config/api';
import { ROUTES } from '@shared/config/routes';
import { ButtonLink, Skeleton } from '@shared/ui';

import { LegalAddress, LegalArticle } from './LegalArticle';

/**
 * Widerrufsbelehrung + Muster-Widerrufsformular.
 *
 * Текст приходит из `GET /config`, а не из локалей, и это существенно: тот же
 * текст дословно уходит в подтверждение договора (§ 312f Abs. 2 BGB). Вторая
 * копия разошлась бы при первой правке, а отклонение от образца Anlage 1 к
 * Art. 246a § 1 Abs. 2 S. 2 EGBGB лишает текст Gesetzlichkeitsfiktion — цена
 * ошибки — срок отзыва до 12 месяцев и 14 дней (§ 356 Abs. 4 BGB).
 *
 * Кнопка «Vertrag widerrufen» стоит **над** текстом: § 356a Abs. 1 BGB требует
 * «hervorgehoben platziert und leicht zugänglich», а не «где-нибудь на
 * странице».
 */
export function WithdrawalPage() {
  const { t } = useTranslation();
  const { data, isPending } = useConfig();

  if (isPending || !data) {
    return (
      <LegalArticle title="Widerrufsbelehrung">
        <Skeleton height="20rem" radius="var(--radius-card)" />
      </LegalArticle>
    );
  }

  const { withdrawal_policy: policy, withdrawal_form_lines: form } = data.legal;

  return (
    <LegalArticle
      title="Widerrufsbelehrung"
      version={data.legal.document_versions.withdrawal}
      lead={
        <>
          <p>{t('legal.withdrawal.lead')}</p>
          <p>
            <ButtonLink to={ROUTES.withdrawalForm}>{t('legal.withdrawal.action')}</ButtonLink>
          </p>
        </>
      }
    >
      {policy.map((section) => (
        <section key={section.title}>
          <h2>{section.title}</h2>
          {section.paragraphs.map((paragraph) =>
            // Многострочный абзац — это адресный блок: он обязан сохранять
            // построчную форму, а не склеиваться в предложение.
            paragraph.includes('\n') ? (
              <LegalAddress key={paragraph} lines={paragraph.split('\n')} />
            ) : (
              <p key={paragraph}>{paragraph}</p>
            ),
          )}
        </section>
      ))}

      <h2>Muster-Widerrufsformular</h2>
      <LegalAddress lines={form} />

      <p>
        {t('legal.withdrawal.electronicHint')}{' '}
        <Link to={ROUTES.withdrawalForm}>{t('legal.withdrawal.action')}</Link>
      </p>
    </LegalArticle>
  );
}
